import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { cn } from "@/utils/cn";
import type {
  IPayrollRunItemEvent,
  IPayrollRunProgress,
} from "@/services/websocket/usePayrollRunSocket";

interface IProps {
  progress: IPayrollRunProgress;
  items: IPayrollRunItemEvent[];
}

export const RunGenerationProgress = ({ progress, items }: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const failedCount = items.filter((i) => i.status === "FAILED").length;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Loader2 className="size-5 text-primary animate-spin" />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900">
            {t("company:payrolls.generating_payroll_2")}
          </span>
          <span className="text-xs text-gray-500">
            {failedCount > 0
              ? t("company:payrolls.employees_processed_progress_with_failed", {
                  processed: progress.processed,
                  total: progress.total || "—",
                  failed: failedCount,
                })
              : t("company:payrolls.employees_processed_progress", {
                  processed: progress.processed,
                  total: progress.total || "—",
                })}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{t("common:shared_ui.progress")}</span>
          <span className="font-semibold text-gray-700">
            {progress.percent}%
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-300"
            style={{
              width: `${Math.min(100, Math.max(0, progress.percent))}%`,
            }}
          />
        </div>
      </div>

      <div className="rounded-xs border border-border bg-white">
        <div className="px-4 py-3 border-b border-border bg-gray-50 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t("company:payrolls.live_log")}
          </span>
          <span className="text-[10px] text-gray-400">
            {t("company:payrolls.event_count", { count: items.length })}
          </span>
        </div>
        <div className="max-h-72 overflow-auto divide-y divide-border">
          {items.length === 0 ? (
            <div className="px-4 py-6 text-xs text-gray-400 text-center">
              {t("company:payrolls.waiting_for_first_item")}
            </div>
          ) : (
            items
              .slice()
              .reverse()
              .map((item) => (
                <div
                  key={`${item.itemId}-${item.userId}`}
                  className="flex items-start gap-2 px-4 py-2"
                >
                  {item.status === "COMPLETED" ? (
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="size-4 text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div className="flex flex-col min-w-0 text-xs">
                    <span
                      className={cn(
                        "font-semibold",
                        item.status === "COMPLETED"
                          ? "text-gray-700"
                          : "text-red-600",
                      )}
                    >
                      {item.status === "COMPLETED"
                        ? t("company:payrolls.employee_item_completed", {
                            id: item.userId,
                          })
                        : t("company:payrolls.employee_item_failed", {
                            id: item.userId,
                          })}
                    </span>
                    {item.error && (
                      <span className="text-red-500 truncate">
                        {item.error}
                      </span>
                    )}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};
import { useTranslation } from "react-i18next";
